import * as React from "react";
import DataDownload from "./DataDownload";
import {
  apiPost,
  buildQueryUrl,
  fetchData,
} from "../common/services/ApiClient";
import { useState } from "react";
import { ApiBaseUrl, AppUrl } from "../config";
import {
  ApiPath,
  Application,
  KeyStringValue,
  KeyValue,
  Query,
  ZipFile,
} from "../interfaces";
import {
  fileNameFormat,
  reduceObj,
  removeDuplicateArrayObj,
  serviceTypeLookup,
  sortObjKeys,
  toTitleCase,
  zipFileDownload,
} from "../common/services/Utilities";
import {
  formatHistoryRecords,
  getHistoryApiFilters,
  getLongDescString,
} from "../common/services/HistoryUtilities";
import { LabelKeyObject } from "react-csv/lib/core";
import { removeDuplicates } from "./SOD/MarketShare/DepositMarketShare/DepositMarketShareUtilities";

interface Props {
  apiData: apiDownloadProps[];
  app: Application;
  apiPathLink?: ApiPath;
  filters?: any;
  downloadData?: KeyValue[] | string[][];
  post?: KeyValue;
  cert?: string;
  name?: string;
  includeAgg?: boolean;
  numberOfResults?: number;
  limit?: number;
  commaSeparatedNumbers?: string[];
  RemoveDuplicateKey?: string;
  bodTotals?: boolean;
}

export interface apiDownloadProps {
  fileName: string;
  apiPath: ApiPath;
  appState: Query;
  downloadUrl?: string;
  downloadLimit?: number;
  formattedData?: KeyValue;
  multiDownload?: string[];
  combineByKey?: string;
  data?: string[][];
  downloadAll?: boolean;
  headers?: DownloadHeaders[];
  text?: string;
  additionalContent?: string[][];
}

export interface DownloadHeaders {
  label: string;
  key: string;
}

export default function ApiDataDownload({
  apiPathLink,
  app,
  apiData,
  filters,
  downloadData,
  post,
  cert,
  name,
  includeAgg,
  numberOfResults,
  limit,
  commaSeparatedNumbers,
  RemoveDuplicateKey,
  bodTotals,
}: Props) {
  const [downloadDataValue, setDownloadDataValue] = useState([{}]);
  const [loadingDownloadData, setLoadingDownloadData] = useState(false);

  const downloadLimit =
    app === "financialReporting" ? 500 : limit ? limit : 10000;
  const numberOfDownloads =
    numberOfResults && numberOfResults > downloadLimit
      ? Math.ceil(numberOfResults / downloadLimit)
      : undefined;

  const apiDownload = apiData.map(data => {
    const dataProvided = data.data || downloadData;
    const filteredHeaders = data.headers
      ? (removeDuplicateArrayObj(data.headers, "key") as LabelKeyObject[])
      : undefined;
    return {
      id: data.downloadAll ? "allCSVDownload" : `${data.apiPath}CSVDownload`,
      linkText: toTitleCase(data.text),
      fileName: data.fileName,
      headers: filteredHeaders,
      data: dataProvided ? dataProvided : downloadDataValue,
      additionalContent: data.additionalContent || undefined,
      onClick: dataProvided
        ? (e: any) => e.currentTarget.offsetParent.click()
        : loadingDownloadData
        ? (e: Event) => setLoadingDownloadData(false)
        : (e: Event) => fetchDownloadData(e, data),
    };
  });

  const definitionLinkLookup: KeyStringValue = {
    institutions: `${AppUrl("API")}/institutions_definitions.csv`,
    history: `${AppUrl("API")}/events_definitions.csv`,
    location: `${AppUrl("API")}/location_definitions.csv`,
    sod_msa: `${AppUrl("API")}/sod_msa_definitions.xlsx`,
  };

  const linkDownload =
    apiPathLink && definitionLinkLookup[apiPathLink]
      ? [
          {
            id: "variableDefinitions",
            text: "Download Variable Definitions",
            href: definitionLinkLookup[apiPathLink],
          },
        ]
      : undefined;

  async function fetchDownloadData(e: any, data: KeyValue) {
    const apiPath = data.apiPath;
    e.preventDefault();
    setLoadingDownloadData(true);
    const currentLink = e.currentTarget as any;
    const historyLink = currentLink.id === "historyCSVDownload";
    const allLink = currentLink.id === "allCSVDownload";
    const linkIndex = apiData
      .map(data => data.fileName)
      .indexOf(currentLink.download);

    const aggVals = includeAgg
      ? undefined
      : {
          agg: {
            agg_by: "",
            agg_term_fields: "" as any,
          },
        };

    const dataDownloadLimit =
      apiData.map(data => data.downloadLimit)[linkIndex] || downloadLimit;

    const maxResultLimit = {
      ...apiData.map(data => data.appState)[linkIndex],
      pagination: {
        resultLimit: dataDownloadLimit,
        pageNumber: 1,
      },
      ...aggVals,
      sort: {
        sortField: "",
        sortOrder: "" as any,
      },
    };

    const downloadURLS = apiData.map(data => data.multiDownload)[linkIndex]
      ? (apiData.map(data => data.multiDownload)[linkIndex] as string[]).map(
          (url: string) =>
            `${url}&limit=${dataDownloadLimit}&agg_limit=${dataDownloadLimit}&offset=0`,
        )
      : [...Array(numberOfDownloads)].map((array: any, int: number) => {
          return apiData.map(data => data.downloadUrl)[linkIndex]
            ? `${
                apiData.map(data => data.downloadUrl)[linkIndex]
              }&limit=${dataDownloadLimit}&offset=${int * dataDownloadLimit}`
            : `${buildQueryUrl(`/${apiPath}`, {
                ...maxResultLimit,
                pagination: {
                  resultLimit: dataDownloadLimit,
                  pageNumber: int + 1,
                },
              })}`;
        });

    const postBody = post
      ? {
          ...post,
          limit: dataDownloadLimit,
          offset: 0,
        }
      : undefined;

    const downloadPath =
      (historyLink && app === "bankFindDetails") || allLink
        ? `${ApiBaseUrl()}/history?${await getHistoryApiFilters(
            filters[0],
            filters[1],
          )}&sort_by=EFFDATE&sort_order=asc&limit=10000&offset=0`
        : apiData.map(data => data.downloadUrl)[linkIndex]
        ? `${
            apiData.map(data => data.downloadUrl)[linkIndex]
          }&limit=${dataDownloadLimit}&offset=0`
        : `${buildQueryUrl(`/${apiPath}`, maxResultLimit)}`;

    const fetch =
      numberOfDownloads || apiData.map(data => data.multiDownload)[linkIndex]
        ? Promise.all(downloadURLS.map((path: string) => fetchData(path)))
        : post && Object.keys(post).length > 0
        ? fetchData(
            `${ApiBaseUrl()}/${apiPathLink ? apiPathLink : "institutions"}`,
            apiPost(postBody),
          )
        : fetchData(downloadPath);

    await fetch
      .then(response => {
        let responseData: any =
          numberOfDownloads ||
          apiData.map(data => data.multiDownload)[linkIndex]
            ? [].concat(...response.map((res: KeyValue) => res.data as never[]))
            : response.data;
        responseData = RemoveDuplicateKey
          ? removeDuplicates(responseData, RemoveDuplicateKey)
          : responseData;
        if (
          apiData.map(data => data.combineByKey)[linkIndex] &&
          apiData.map(data => data.multiDownload)[linkIndex]
        ) {
          //combines multiple arrays into one
          const combinedRes = [].concat.apply(
            [],
            responseData.map((data: KeyValue) =>
              //finds the matching value based off of the key and assigns both to an object
              Object.assign(
                {},
                ...responseData.filter(
                  (val: KeyValue) =>
                    val[
                      apiData.map(data => data.combineByKey)[
                        linkIndex
                      ] as string
                    ] ===
                    data[
                      apiData.map(data => data.combineByKey)[
                        linkIndex
                      ] as string
                    ],
                ),
              ),
            ),
          );

          //sets response to the combined array, removing the duplicates based off of the length of the multi download
          responseData = combinedRes.splice(
            0,
            combinedRes.length /
              (apiData.map(data => data.multiDownload)[linkIndex] as string[])
                .length,
          );
        }

        if (commaSeparatedNumbers) {
          responseData = responseData.map((data: KeyValue) => {
            const formattedValues = Object.keys(data).map((key: string) => {
              return commaSeparatedNumbers.includes(key) && data[key]
                ? { key: key, value: data[key].toLocaleString() }
                : { key: key, value: data[key] };
            });
            return reduceObj(formattedValues);
          });
        }
        if (app === "SOD") {
          responseData = responseData.map((data: KeyValue) => {
            return {
              ...data,
              BRSERTYPDESC: serviceTypeLookup(data.BRSERTYP),
              YEAR: `6/30/${data.YEAR}`,
            };
          });
        }
        if (responseData[0]?.CHANGECODE_DESC) {
          const descArray: any = formatHistoryRecords(responseData)
            .map(data => data.CHANGECODE_DESC_LONG)
            .map((desc: any) => {
              return getLongDescString(desc);
            });

          responseData = responseData.map((data: KeyValue, index: number) => {
            return { ...data, CHANGECODE_DESC_LONG: descArray[index] };
          });
        }

        const downloadData = data.formattedData
          ? data.formattedData
          : responseData.map((data: KeyValue) => sortObjKeys(data));

        if (bodTotals) {
          let totals = {
            NAMEFULL: "Totals",
            DEPSUMBR: downloadData
              .reduce((accumulator: any, currentValue: any) => {
                return (
                  accumulator +
                  Number(String(currentValue.DEPSUMBR).replaceAll(",", ""))
                );
              }, 0)
              .toLocaleString(),
          };
          downloadData.push(totals);
        }

        setDownloadDataValue(downloadData);

        if (allLink) {
          const zipFiles: ZipFile[] = [
            {
              url: `${buildQueryUrl(
                `/institutions`,
                maxResultLimit,
              )}&format=csv&download=true`,
              name: fileNameFormat("Institutions", cert, name),
            },
            {
              url: `${buildQueryUrl(`/locations`, {
                ...maxResultLimit,
                filters: {
                  cert: maxResultLimit.filters.cert,
                  stalp: maxResultLimit.filters.stalp,
                  zip: maxResultLimit.filters.zip,
                },
                search: {
                  city: maxResultLimit.search.city,
                },
              })}&format=csv&download=true`,
              name: fileNameFormat("Locations", cert, name),
            },
            {
              url: currentLink.href,
              name: fileNameFormat("History", cert, name),
            },
          ];
          zipFileDownload(zipFiles, fileNameFormat("All_Files", cert, name));
          setLoadingDownloadData(false);
        } else {
          currentLink.click();
        }
      })
      .catch((error: Error) => {
        console.error(error);
      })
      .finally(() => {
        if (currentLink.offsetParent) {
          currentLink.offsetParent.click();
          setDownloadDataValue([{}]);
          setLoadingDownloadData(false);
        }
      });
  }
  return (
    <DataDownload
      apiDownload={apiDownload}
      loading={loadingDownloadData}
      downloadLinks={linkDownload}
      numberOfResults={numberOfResults}
      downloadLimit={downloadLimit}
    />
  );
}
